export type CatalogKind = "first-party" | "hosted" | "external" | "creator-release";

/** Extend as adapters are added (Phase 3: js13k, tic-80, scratch, scummvm, ruffle...). */
export type CatalogSourceId = "carcade" | "rosebud" | "web" | "js13k";

/**
 * A cover is either present with a real URL, or explicitly missing with a
 * reason -- never a broken <img> src standing in for "we don't have one."
 */
export type CatalogCover =
  | { readonly status: "present"; readonly url: string }
  | { readonly status: "missing"; readonly reason: "not-yet-catalogued" | "source-removed-cover" | "health-check-failed" };

export interface CatalogPlayerRange { readonly min: number; readonly max: number; }
export type CatalogPlayers =
  | { readonly status: "known"; readonly range: CatalogPlayerRange }
  | { readonly status: "unknown" };

export type CatalogInputKind = "touch" | "keyboard" | "mouse" | "gamepad";
export type CatalogOfflineCapability = "capable" | "online-only" | "unknown";

/**
 * Reversible, never a delete flag. "excluded" retains the source record but
 * never surfaces publicly. "broken" is a health-check finding, not a
 * removal -- it still surfaces so unavailable destinations stay explicit.
 */
export type CatalogEntryStatus = "active" | "hidden" | "excluded" | "broken";

interface CatalogEntryCommon {
  readonly catalogId: string;
  /** Defaults to catalogId when the entry has no known duplicate versions. */
  readonly groupId: string;
  readonly source: CatalogSourceId;
  /** The ID as known within its own source: GameManifest.id, or a Rosebud slug. */
  readonly sourceItemId: string;
  readonly title: string;
  readonly description: string | null;
  readonly cover: CatalogCover;
  readonly categories: readonly string[];
  readonly players: CatalogPlayers;
  readonly inputs: readonly CatalogInputKind[];
  readonly offline: CatalogOfflineCapability;
  readonly status: CatalogEntryStatus;
  /** Pointer to the durable review/inclusion evidence for this entry. */
  readonly receiptRef: string | null;
  readonly createdAt: number;
  readonly updatedAt: number;
}

export interface FirstPartyCatalogEntry extends CatalogEntryCommon {
  readonly kind: "first-party";
  readonly launch: { readonly gameId: string };
}

export interface HostedCatalogEntry extends CatalogEntryCommon {
  readonly kind: "hosted";
  readonly launch: { readonly gameId: string };
}

export interface ExternalCatalogEntry extends CatalogEntryCommon {
  readonly kind: "external";
  readonly launch: {
    readonly url: `https://${string}`;
    readonly slug: string;
    /**
     * The source's own game-server origin, when harvested (see
     * scripts/scrape-rosebud.mts) -- lets ExternalOverlay frame the game
     * directly instead of Rosebud's page chrome. Absent for entries where
     * harvesting failed or hasn't run; falls back to `url` at launch time.
     */
    readonly gatewayUrl?: string;
  };
}

export interface CreatorReleaseCatalogEntry extends CatalogEntryCommon {
  readonly kind: "creator-release";
  readonly launch: { readonly releaseId: string };
}

export type CatalogEntry =
  | FirstPartyCatalogEntry
  | HostedCatalogEntry
  | ExternalCatalogEntry
  | CreatorReleaseCatalogEntry;

/**
 * Fixed, curated taxonomy for the launcher's category browse -- not derived
 * from source-site tags. Games are assigned into these by
 * scripts/catalog-categories.ts; "other" is an honest catch-all, not a
 * dumping ground for entries nobody looked at (see that file's review path).
 */
export const CATALOG_CATEGORIES = [
  "racing",
  "sports",
  "puzzle",
  "action",
  "building",
  "adventure",
  "multiplayer",
  "creative",
  "cards-board",
  "other",
] as const;

export type CatalogCategoryId = (typeof CATALOG_CATEGORIES)[number];
